/**
 * Product Page Quantity Selector - Uses CSS variable height
 * Handles the "Dropdown to Input" transition for 10+ quantities.
 */
class QuantitySelectorProduct {
    /**
     * @param {HTMLElement} container
     */
    constructor(container) {
        /** @type {HTMLElement} */
        this.container = container;
        const settingsEl = container.querySelector('[data-settings-product]');
        if (!settingsEl) return;
        this.settings = JSON.parse(settingsEl.textContent || '{}');
        this.init();
    }

    init() {
        /** @type {HTMLSelectElement | null} */
        this.select = this.container.querySelector('[data-quantity-select-product]');
        /** @type {HTMLInputElement | null} */
        this.input = this.container.querySelector('[data-quantity-input-product]');
        /** @type {HTMLElement | null} */
        this.minusBtn = this.container.querySelector('[data-quantity-minus-product]');
        /** @type {HTMLElement | null} */
        this.plusBtn = this.container.querySelector('[data-quantity-plus-product]');

        if (this.select) {
            this.select.addEventListener('change', (e) => this.handleSelectChange(e));
        }

        if (this.input) {
            this.input.addEventListener('change', (e) => {
                this.handleInputChange(e);
                this.updateInputWidth();
            });
            this.input.addEventListener('input', (e) => {
                const target = /** @type {HTMLInputElement} */ (e.target);
                if (target.value === '') return;
                const val = parseInt(target.value);
                if (!isNaN(val) && val < 0) target.value = '0';
                this.updateInputWidth();
            });
            this.updateInputWidth();
        }

        if (this.minusBtn) {
            this.minusBtn.addEventListener('click', () => this.updateQuantity(-1));
        }

        if (this.plusBtn) {
            this.plusBtn.addEventListener('click', () => this.updateQuantity(1));
        }
    }

    /**
     * @param {number} delta
     */
    updateQuantity(delta) {
        if (!this.input) return;
        const currentVal = parseInt(this.input.value) || 0;
        const newVal = currentVal + delta;

        const limit = this.settings.dropdownLimit || 9;
        if (newVal <= limit) {
            this.convertToSelect(newVal < this.settings.min ? this.settings.min : newVal);
        } else {
            if (this.settings.max === null || newVal <= this.settings.max) {
                this.input.value = newVal.toString();
                this.updateInputWidth();
                this.triggerChange(newVal);
            }
        }
    }

    /**
     * @param {Event} e
     */
    handleSelectChange(e) {
        const target = /** @type {HTMLSelectElement} */ (e.target);
        const limit = this.settings.dropdownLimit || 9;
        if (target.value === (limit + 1) + '+') {
            this.convertToInput(limit + 1);
        } else {
            this.triggerChange(parseInt(target.value));
        }
    }

    /**
     * @param {Event} e
     */
    handleInputChange(e) {
        const target = /** @type {HTMLInputElement} */ (e.target);
        let val = parseInt(target.value);
        if (isNaN(val)) val = this.settings.min;

        const limit = this.settings.dropdownLimit || 9;
        if (val <= limit) {
            this.convertToSelect(val < this.settings.min ? this.settings.min : val);
        } else {
            if (this.settings.max !== null && val > this.settings.max) {
                val = this.settings.max;
                target.value = val.toString();
            }
            this.triggerChange(val);
        }
    }

    /**
     * @param {number} value
     */
    convertToInput(value) {
        const inputHtml = `
      <div class="flex items-center" style="height: var(--height-buy-buttons);">
        <div class="relative" style="height: var(--height-buy-buttons);">
          <input
            type="number"
            name="${this.settings.name}"
            id="${this.settings.id}"
            value="${value}"
            min="${this.settings.lineIndex !== null ? '0' : (this.settings.min || 1)}"
            ${this.settings.max !== null ? `max="${this.settings.max}"` : ''}
            step="1"
            class="rounded-bl-lg border border-gray-300 bg-white px-2 text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#22b573] text-center m-0 relative z-10"
            style="min-width: 36px; height: var(--height-buy-buttons);"
            ${this.settings.lineIndex !== null ? `data-cart-line="${this.settings.lineIndex + 1}"` : ''}
            data-quantity-input-product
          >
        </div>

        <div class="flex flex-col w-9 border border-gray-300 border-l-0 rounded-tr-lg overflow-hidden" style="height: var(--height-buy-buttons);">
          <button 
            type="button" 
            class="flex-1 flex items-center justify-center bg-white text-gray-600 hover:bg-[#22b573]/10 hover:text-[#22b573] transition-colors focus:outline-none border-b border-gray-200"
            data-quantity-plus-product
            aria-label="Vermeerder aantal"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 4v16m8-8H4"></path>
            </svg>
          </button>
          <button 
            type="button" 
            class="flex-1 flex items-center justify-center bg-white text-gray-600 hover:bg-[#22b573]/10 hover:text-[#22b573] transition-colors focus:outline-none"
            data-quantity-minus-product
            aria-label="Verminder aantal"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M20 12H4"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
        this.container.innerHTML = inputHtml + `<script type="application/json" data-settings-product>${JSON.stringify(this.settings)}</script>`;
        this.init();

        if (this.input) {
            this.updateInputWidth();
            this.input.focus();
            const val = this.input.value;
            this.input.value = '';
            this.input.value = val;
        }
        this.triggerChange(value);
    }

    updateInputWidth() {
        if (!this.input) return;
        const len = this.input.value.length || 1;
        this.input.style.width = (len + 2) + 'ch';
    }

    /**
     * @param {number} value
     */
    convertToSelect(value) {
        let options = '';
        const limit = this.settings.dropdownLimit || 9;
        const startVal = this.settings.min || 1;
        for (let i = startVal; i <= limit; i++) {
            options += `<option value="${i}" ${i === value ? 'selected' : ''}>${i}</option>`;
        }
        const plusVal = limit + 1;
        options += `<option value="${plusVal}+">${plusVal}+</option>`;

        const selectHtml = `
      <div class="relative" style="height: var(--height-buy-buttons);">
        <select
          name="${this.settings.name}"
          id="${this.settings.id}"
          class="rounded-bl-lg border border-gray-300 bg-white px-2 text-xs font-bold text-gray-900 focus:outline-none focus:ring-1 focus:ring-[#22b573] text-center m-0 relative z-10"
          style="height: var(--height-buy-buttons);"
          ${this.settings.lineIndex !== null ? `data-cart-line="${this.settings.lineIndex + 1}"` : ''}
          data-quantity-select-product
        >
          ${options}
        </select>
      </div>
    `;
        this.container.innerHTML = selectHtml + `<script type="application/json" data-settings-product>${JSON.stringify(this.settings)}</script>`;
        this.init();

        this.triggerChange(value);
    }

    /**
     * @param {number} value
     */
    triggerChange(value) {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.container.dispatchEvent(new CustomEvent('quantity:change', {
                bubbles: true,
                detail: {
                    value: value,
                    name: this.settings.name,
                    lineIndex: this.settings.lineIndex
                }
            }));

            if (this.settings.lineIndex !== null) {
                this.container.dispatchEvent(new CustomEvent('quantity-selector:update', {
                    bubbles: true,
                    detail: {
                        quantity: value,
                        cartLine: this.settings.lineIndex + 1
                    }
                }));
            }
        }, 1000);
    }
}

function initQuantitySelectorsProduct() {
    document.querySelectorAll('[data-quantity-selector-product]').forEach(el => {
        const container = /** @type {HTMLElement} */ (el);
        if (!container.dataset.initialized) {
            new QuantitySelectorProduct(container);
            container.dataset.initialized = 'true';
        }
    });
}

document.addEventListener('DOMContentLoaded', initQuantitySelectorsProduct);
const cartEventsProduct = ['cart:update', 'cart:refresh', 'theme:cart:update', 'cart-update', 'cart:updated'];
cartEventsProduct.forEach(eventName => {
    document.addEventListener(eventName, () => {
        setTimeout(initQuantitySelectorsProduct, 50);
    });
});
document.addEventListener('shopify:section:load', initQuantitySelectorsProduct);
document.addEventListener('page:updated', initQuantitySelectorsProduct);
